import { sortBy } from "lodash";
import React, { ReactNode, useEffect, useMemo } from "react";
import { CollectionResource } from "types";
import { useVisibility } from "../../hooks/useVisibility";
import { SecondaryButton } from "./SecondaryButton";

const MAX_DEFAULT_ENTITIES_TO_DISPLAY = 5;

interface ExpandableListProps<T> {
    entities: T[];
    component: (params: T, index: number) => JSX.Element;
    children?: ReactNode;
    styles?: {
        container?: { className?: string };
        button?: { className?: string; size?: "tiny" | "small" | "medium" | "large"; counter?: boolean };
    };
    sortField?: string;
    hideOnChange?: boolean;
}

export const ExpandableList = <T extends CollectionResource | string>({
    entities,
    component,
    children,
    styles,
    sortField,
    hideOnChange = false,
}: ExpandableListProps<T>) => {
    const [expanded, { toggle, hide }] = useVisibility(false);

    const sortedEntities = useMemo(() => {
        return sortField ? sortBy(entities, sortField) : entities;
    }, [sortField, entities]);

    const needsMinimization = sortedEntities.length > MAX_DEFAULT_ENTITIES_TO_DISPLAY;

    useEffect(() => {
        if (hideOnChange && expanded) {
            hide();
        }
        // The `expanded` shouldn't be added since it will close the list once it's open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hide, entities, hideOnChange]);

    const visibleEntities = useMemo(
        () => (expanded ? sortedEntities : sortedEntities.slice(0, MAX_DEFAULT_ENTITIES_TO_DISPLAY)),
        [expanded, sortedEntities],
    );

    const { size: buttonSize = "small", counter: buttonCounter = true } = styles?.button ?? {};

    return (
        <>
            <div className={styles?.container?.className}>
                {visibleEntities.map((entity, index) => component(entity, index))}
                {children}
            </div>

            {needsMinimization && (
                <SecondaryButton className={styles?.button?.className} size={buttonSize} onClick={toggle}>
                    {expanded ? (
                        "Show Less"
                    ) : (
                        <span>
                            Show All <var data-var="count">{buttonCounter ? `(${entities.length})` : ""}</var>
                        </span>
                    )}
                </SecondaryButton>
            )}
        </>
    );
};
